import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, Users, Factory, UserCog, Box, 
  ShoppingCart, Calculator, ClipboardCheck, ChevronLeft,
  ChevronRight, Home, LogOut, Lock, CreditCard, Receipt,
  Package, Calendar, Shield, ShieldCheck, Navigation, Settings, Brain, Globe, Building2, Truck, Award, Hammer, Wallet, BookOpen, UserCircle, CalendarDays, BarChart3, ChevronDown, MessageSquare, ClipboardList, Scale, Database, Activity, Tag, FileText, Target, DollarSign, TrendingUp, UserPlus, AlertTriangle, Banknote, ShoppingBag, Menu, X, KeyRound
} from 'lucide-react';
import { resolveModulesForUser } from '../utils/roles';
import ERPNotificationBell from './ERPNotificationBell';

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_0aec802e-e67b-4582-8fac-1517907b7262/artifacts/752tez4i_Logo%20Cucumaa%20Glass.png";

// Role-based access configuration - single source of truth in src/utils/roles.js
// (resolveRoleModules imported above)

const ERPLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = { management: false, sales: false, operations: false, finance: false, settings: false };
    return initial;
  });

  // Close the mobile drawer whenever the route changes
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Track desktop breakpoint so the collapse toggle only applies on desktop
  const [isDesktop, setIsDesktop] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  React.useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Collapse only affects the fixed desktop sidebar
  const effCollapsed = isDesktop && collapsed;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Get allowed modules for current user
  const userRole = user?.role || 'customer';
  const allowedModules = resolveModulesForUser(user);

  // Sales role sidebar order — sales@lucumaa.in must see the funnel in this order:
  // Lead Integration, CRM & Sales, Quotations, Sales Invoice, Sales Task, Field Sales, Abandoned Carts.
  const SALES_MENU_ORDER = ['leads', 'crm', 'quotations', 'salesinvoice', 'salestasks', 'sfa', 'abandonedcarts'];

  // Organized menu sections
  const menuSections = [
    {
      id: 'management',
      label: 'Management',
      items: [
        { path: '/erp/superadmin', key: 'superadmin', icon: Shield, label: 'Super Admin', color: 'text-red-400' },
        { path: '/erp/customer-master', key: 'customermaster', icon: UserCircle, label: 'Customer Master', color: 'text-blue-400' },
        { path: '/erp/superadmin/users', key: 'superadmin_users', icon: Users, label: 'User Management', color: 'text-blue-400' },
        { path: '/erp/superadmin/audit', key: 'superadmin_audit', icon: FileText, label: 'Audit & Activity', color: 'text-purple-400' },
        { path: '/erp/superadmin/permissions', key: 'superadmin_permissions', icon: ShieldCheck, label: 'Permission Control', color: 'text-amber-400' },
        { path: '/erp/superadmin/credentials', key: 'superadmin_credentials', icon: KeyRound, label: 'Credentials', color: 'text-emerald-400' },
        { path: '/erp/admin', key: 'admin', icon: LayoutDashboard, label: 'Dashboard', color: 'text-blue-400' },
        { path: '/erp/inquiries', key: 'inquiries', icon: MessageSquare, label: 'Inquiries', color: 'text-pink-400' },
        { path: '/erp/rewards', key: 'rewards', icon: Award, label: 'Rewards & Referrals', color: 'text-purple-400' },
        { path: '/erp/assets', key: 'assets', icon: Box, label: 'Assets', color: 'text-teal-400' },
      ]
    },
    {
      id: 'sales',
      label: 'Sales & CRM',
      items: [
        { path: '/erp/orders', key: 'orders', icon: Package, label: 'Order Management', color: 'text-emerald-400' },
        { path: '/erp/sales-invoice', key: 'salesinvoice', icon: FileText, label: 'Sales Invoice', color: 'text-cyan-400' },
        { path: '/erp/purchase-invoice', key: 'purchasemodule', icon: ShoppingBag, label: 'Purchase Invoice', color: 'text-orange-400' },
        { path: '/erp/quotations', key: 'quotations', icon: FileText, label: 'Quotations', color: 'text-cyan-400' },
        { path: '/erp/discount-approvals', key: 'discountapprovals', icon: Tag, label: 'Discount Approvals', color: 'text-amber-400' },
        { path: '/erp/leads', key: 'leads', icon: Users, label: 'Lead Integration', color: 'text-blue-400' },
        { path: '/erp/abandoned-carts', key: 'abandonedcarts', icon: ShoppingCart, label: 'Abandoned Carts', color: 'text-orange-400' },
        { path: '/erp/sales-tasks', key: 'salestasks', icon: ClipboardList, label: 'Sales Tasks', color: 'text-teal-400' },
        { path: '/erp/sales-targets', key: 'salestargets', icon: Target, label: 'Sales Targets', color: 'text-green-400' },
        { path: '/erp/incentives', key: 'incentives', icon: DollarSign, label: 'Incentives', color: 'text-yellow-400' },
        { path: '/erp/performance', key: 'performance', icon: TrendingUp, label: 'Performance', color: 'text-orange-400' },
        { path: '/erp/sfa', key: 'sfa', icon: Navigation, label: 'Field Sales (SFA)', color: 'text-lime-400' },
        { path: '/erp/crm', key: 'crm', icon: Users, label: 'CRM & Sales', color: 'text-emerald-400' },
      ]
    },
    {
      id: 'operations',
      label: 'Operations',
      items: [
        { path: '/erp/production', key: 'production', icon: Factory, label: 'Production', color: 'text-orange-400' },
        { path: '/erp/operator', key: 'operator', icon: ClipboardCheck, label: 'Operator', color: 'text-violet-400' },
        { path: '/erp/inventory', key: 'inventory', icon: Box, label: 'Inventory', color: 'text-cyan-400' },
        { path: '/erp/floor-manager', key: 'floormanager', icon: ClipboardList, label: 'Floor Manager', color: 'text-cyan-400' },
        { path: '/erp/transport', key: 'transport', icon: Truck, label: 'Transport', color: 'text-orange-400' },
        { path: '/erp/driver-dashboard', key: 'driverdashboard', icon: Truck, label: 'My Deliveries', color: 'text-amber-500' },
        { path: '/erp/job-work', key: 'jobwork', icon: Hammer, label: 'Job Work', color: 'text-orange-400' },
      ]
    },
    {
      id: 'hr',
      label: 'HR & Admin',
      items: [
        { path: '/erp/onboarding', key: 'onboarding', icon: UserPlus, label: 'Employee Onboarding', color: 'text-teal-400' },
        { path: '/erp/hr', key: 'hr', icon: UserCog, label: 'HR & Payroll', color: 'text-indigo-400' },
        { path: '/erp/hr-letters', key: 'hrletters', icon: AlertTriangle, label: 'HR Letters', color: 'text-orange-400' },
        { path: '/erp/leave', key: 'leave', icon: CalendarDays, label: 'Leave Management', color: 'text-green-400' },
        { path: '/erp/holidays', key: 'holidays', icon: Calendar, label: 'Holidays', color: 'text-yellow-400' },
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      items: [
        { path: '/erp/accounts', key: 'accounts', icon: Calculator, label: 'Accounts', color: 'text-amber-400' },
        { path: '/erp/payments', key: 'payments', icon: Banknote, label: 'Payment Tracking', color: 'text-emerald-400' },
        { path: '/erp/ledger', key: 'ledger', icon: BookOpen, label: 'Ledger & GL', color: 'text-emerald-400' },
        { path: '/erp/payouts', key: 'payouts', icon: CreditCard, label: 'Payouts', color: 'text-green-400' },
        { path: '/erp/expenses', key: 'expenses', icon: Receipt, label: 'Expenses', color: 'text-red-400' },
        { path: '/erp/gst', key: 'gst', icon: Receipt, label: 'GST Management', color: 'text-blue-400' },
        { path: '/erp/refunds', key: 'refunds', icon: Receipt, label: 'Refunds & Cancellations', color: 'text-red-400' },
        { path: '/erp/vendor-management', key: 'vendors', icon: Wallet, label: 'Vendor & PO', color: 'text-violet-400' },
      ]
    },
    {
      id: 'settings',
      label: 'Settings & Tools',
      items: [
        { path: '/erp/forecast', key: 'forecast', icon: Brain, label: 'Demand Forecast', color: 'text-cyan-400' },
        { path: '/erp/cms', key: 'cms', icon: Globe, label: 'CMS', color: 'text-indigo-400' },
        { path: '/erp/branches', key: 'branches', icon: Building2, label: 'Branches', color: 'text-rose-400' },
        { path: '/erp/product-config', key: 'productconfig', icon: Settings, label: 'Product Config', color: 'text-amber-400' },
        { path: '/erp/glass-weight', key: 'glassweight', icon: Scale, label: 'Glass Weight Settings', color: 'text-teal-400' },
        { path: '/erp/tally-settings', key: 'tally', icon: Database, label: 'Tally Integration', color: 'text-purple-400' },
        { path: '/erp/settings', key: 'settings', icon: Settings, label: 'Payment Settings', color: 'text-gray-400' },
      ],
    },
  ];

  // RETIRED FROM NAVIGATION (intentionally absent from menuSections above):
  //  - Pin Code Master — only ever drove delivery-pincode validation and
  //    shipping-zone rules. No part of orders, invoicing, production,
  //    dispatch or inventory reads it, so the menu entry implied a
  //    dependency that did not exist. The backend collection is untouched.
  //  - Automation — the rule engine was never wired into any live flow.
  //  - AI Demand Forecast — folded into the single accurate Demand Forecast.
  //  - Activity Report / Activity Log — folded into Audit & Activity.

  // On role/mount, open ONLY the first section that has any allowed module;
  // every other dropdown section stays closed until the user expands it.
  React.useEffect(() => {
    if (allowedModules.length === 0) return;
    const firstVisible = menuSections.find((sec) =>
      sec.items.some((item) => allowedModules.includes(item.key))
    );
    if (!firstVisible) return;
    setExpandedSections(() => {
      const next = { management: false, sales: false, operations: false, finance: false, settings: false };
      next[firstVisible.id] = true;
      return next;
    });
    // Only re-run when the role changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  // Derive current module + page title from the active route
  const currentItem = menuSections
    .flatMap((s) => s.items)
    .find((item) => location.pathname === item.path)
    || (location.pathname === '/erp/dashboard' ? { label: 'Dashboard' } : null);
  const pageTitle = currentItem?.label || (location.pathname === '/erp' ? 'ERP' : 'Module');
  const currentModule = pageTitle;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // If no user or no ERP access, show access denied
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center p-8">
          <Lock className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Login Required</h2>
          <p className="text-slate-400 mb-6">Please login to access the ERP system</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-500 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (allowedModules.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center p-8">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400 mb-6">Your role ({userRole}) doesn't have access to the ERP system</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            Back to Website
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - off-canvas on mobile, fixed on desktop */}
      <aside
        className={`fixed left-0 top-0 z-50 h-full bg-white border-r border-slate-200 shadow-sm transition-transform duration-300 lg:transition-all ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${
          effCollapsed ? 'lg:w-20' : 'lg:w-64'
        } w-64`}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center justify-between px-4 border-b border-slate-100 ${effCollapsed ? 'justify-center px-2' : ''}`}>
          <div className={`flex items-center gap-2.5 ${effCollapsed ? 'mx-auto' : ''}`}>
            <img
              src={COMPANY_LOGO}
              alt="Lucumaa Glass"
              className="w-9 h-9 object-contain rounded-xl p-1 ring-1 ring-slate-100 bg-white"
            />
            {!effCollapsed && (
              <div className="leading-tight">
                <span className="font-bold text-[15px] text-slate-800">Lucumaa</span>
                <p className="text-[10px] text-slate-400 -mt-0.5 tracking-wide">ERP System</p>
              </div>
            )}
          </div>
          {/* Mobile close */}
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-600 ml-auto">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Desktop Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-16 w-6 h-6 bg-white rounded-full items-center justify-center hover:bg-slate-100 transition-colors shadow border border-slate-200 text-slate-500"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-180px)]">
          {menuSections.map((section) => {
            const visibleItems = section.items
              .filter(item =>
                allowedModules.includes(item.key) &&
                !(item.key === 'driverdashboard' && (userRole || '').toLowerCase() !== 'driver')
              )
              .sort((a, b) => {
                if (userRole !== 'sales') return 0;
                const ia = SALES_MENU_ORDER.indexOf(a.key);
                const ib = SALES_MENU_ORDER.indexOf(b.key);
                if (ia === -1 && ib === -1) return 0;
                if (ia === -1) return 1;
                if (ib === -1) return -1;
                return ia - ib;
              });
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.id} className="mb-2">
                {!effCollapsed && (
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600"
                  >
                    <span>{section.label}</span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${expandedSections[section.id] ? '' : '-rotate-90'}`}
                    />
                  </button>
                )}
                {(effCollapsed || expandedSections[section.id]) && (
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;

                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                            isActive
                              ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/20 font-medium'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                          }`}
                          title={effCollapsed ? item.label : ''}
                        >
                          <div className={`flex-shrink-0 ${isActive ? 'text-white' : item.color} transition-colors`}>
                            <Icon className="w-[18px] h-[18px]" />
                          </div>
                          {!effCollapsed && (
                            <span className="text-[13px] font-medium">{item.label}</span>
                          )}
                          {!effCollapsed && isActive && (
                            <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full" />
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`erp-main flex-1 transition-all duration-300 ${effCollapsed ? 'lg:ml-20' : 'lg:ml-64'} bg-slate-50`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-3 sm:px-6 py-3 backdrop-blur backdrop-saturate-150">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden sm:block h-8 w-1 rounded-full bg-gradient-to-b from-teal-500 to-emerald-600"></div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{pageTitle}</p>
              <p className="text-[11px] text-slate-400 capitalize truncate">{currentModule}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <NavLink
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <Home className="h-4 w-4" /> Website
            </NavLink>
            <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
            {!['customer', 'dealer', 'driver'].includes(userRole) && (
              <>
                <ERPNotificationBell />
                <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
              </>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
            <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 text-sm font-bold text-white shadow-sm">
                {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="hidden lg:block leading-tight">
                <p className="text-sm font-medium text-slate-800 truncate max-w-[160px]">{user?.name || user?.email}</p>
                <p className="text-[11px] text-teal-600 capitalize">{userRole.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </header>

        <div className="min-h-screen">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ERPLayout;
